import React, { useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Logout } from '@mui/icons-material';
import { ColorModeContext } from '../App';
import { SegmentedToggle } from '../components/SegmentedToggle';
import { userInitial } from '../components/UserMenu';
import { LEGAL_CONTACT_EMAIL } from '../content/legal';
import {
  AccountProfile,
  deleteAccount,
  getAccount,
  resendVerificationEmail,
  sendPasswordReset,
  updateAccount,
} from '../services/api';
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from '../services/authToken';

// Value calculator preferences kept in this browser (see BetValuePanel).
const BANKROLL_KEY = 'ltb.bankroll';
const KELLY_KEY = 'ltb.kellyFraction';
const readStored = (k: string) => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};

const providerLabel = (p: string) =>
  p === 'password' ? 'Email and password' : p ? `${p.charAt(0).toUpperCase()}${p.slice(1)} sign-in` : 'Sign-in';

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : undefined;

/** One labeled row of a settings section: label and value on the left, an action on the right. */
const Row: React.FC<{ label: string; value?: React.ReactNode; action?: React.ReactNode; children?: React.ReactNode }> = ({
  label,
  value,
  action,
  children,
}) => (
  <Box sx={{ py: 2 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {value !== undefined && (
          <Typography variant="body1" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
    {children}
  </Box>
);

const Section: React.FC<{ title: string; description?: string; danger?: boolean; children: React.ReactNode }> = ({
  title,
  description,
  danger,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, ...(danger && { borderColor: 'error.main' }) }}>
    <Typography variant="h6" sx={{ fontWeight: 650, color: danger ? 'error.main' : undefined }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    )}
    <Divider sx={{ mt: 1.5 }} />
    <Box sx={{ '& > div:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}>{children}</Box>
  </Paper>
);

/** Inline edit for a single text field (name or email). */
const EditableField: React.FC<{
  label: string;
  value: string;
  type?: string;
  helper?: string;
  disabledReason?: string;
  onSave: (v: string) => Promise<unknown>;
}> = ({ label, value, type = 'text', helper, disabledReason, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(value), [value]);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <Row label={label}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && draft.trim() && save()}
            error={!!error}
            helperText={error ?? helper ?? ' '}
            autoFocus
            fullWidth
            sx={{ maxWidth: 420 }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving || !draft.trim() || draft.trim() === value}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
            <Button
              onClick={() => {
                setEditing(false);
                setDraft(value);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Row>
    );
  }
  return (
    <Row
      label={label}
      value={value || '—'}
      action={
        disabledReason ? (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260, display: 'block', textAlign: { sm: 'right' } }}>
            {disabledReason}
          </Typography>
        ) : (
          <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )
      }
    />
  );
};

const Profile: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [calcPrefs, setCalcPrefs] = useState(() => ({ bankroll: readStored(BANKROLL_KEY), kelly: readStored(KELLY_KEY) }));

  const account = useQuery<AccountProfile, Error>({
    queryKey: ['account'],
    queryFn: getAccount,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  });

  // Fall back to the sign-in token's details when the server can't manage accounts (or is unreachable).
  const managed = account.data?.managementEnabled === true;
  const subProvider = (user?.sub ?? '').split('|')[0];
  const fallbackProvider = subProvider === 'auth0' ? 'password' : subProvider.replace(/-oauth2$/, '');
  const profile: AccountProfile = managed
    ? account.data!
    : {
        managementEnabled: false,
        email: user?.email ?? '',
        emailVerified: !!user?.email_verified,
        name: user?.name ?? '',
        picture: user?.picture ?? '',
        provider: fallbackProvider,
        canChangePassword: fallbackProvider === 'password',
        canChangeEmail: false,
        canChangeName: false,
      };
  const isPassword = profile.provider === 'password';
  const providerName = providerLabel(profile.provider).replace(' sign-in', '');

  const passwordReset = useMutation({
    mutationFn: async () => {
      if (managed) return sendPasswordReset();
      // Without server-side account management, ask Auth0 directly (public endpoint; database accounts only).
      const r = await fetch(`https://${AUTH0_DOMAIN}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: AUTH0_CLIENT_ID, email: profile.email, connection: 'Username-Password-Authentication' }),
      });
      if (!r.ok) throw new Error("Couldn't send the email right now");
      return { sent: true, email: profile.email };
    },
    onSuccess: (r) => setNotice({ severity: 'success', text: `We sent a password reset link to ${r.email}.` }),
    onError: (e: Error) => setNotice({ severity: 'error', text: e.message }),
  });
  const verify = useMutation({
    mutationFn: resendVerificationEmail,
    onSuccess: () => setNotice({ severity: 'success', text: `We sent a new verification email to ${profile.email}.` }),
    onError: (e: Error) => setNotice({ severity: 'error', text: e.message }),
  });
  const remove = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => logout({ logoutParams: { returnTo: window.location.origin } }),
    onError: (e: Error) => setNotice({ severity: 'error', text: e.message }),
  });
  const save = async (changes: { name?: string; email?: string }) => {
    const updated = await updateAccount(changes);
    queryClient.setQueryData(['account'], updated);
    setNotice({
      severity: 'success',
      text: changes.email
        ? `Email updated. Check ${changes.email} for a verification link.`
        : 'Name updated. It will show everywhere next time you sign in.',
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  const unmanagedReason = `To change this, contact ${LEGAL_CONTACT_EMAIL}.`;
  const socialReason = `Managed by your ${providerName} account.`;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Avatar
            src={profile.picture || undefined}
            alt={profile.name || profile.email}
            imgProps={{ referrerPolicy: 'no-referrer' }}
            sx={{ width: 88, height: 88, fontSize: 36, bgcolor: 'primary.main', color: 'primary.contrastText' }}
          >
            {userInitial({ name: profile.name, email: profile.email })}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              {profile.name || profile.email}
            </Typography>
            <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {profile.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                icon={profile.emailVerified ? <CheckCircle /> : <ErrorOutline />}
                color={profile.emailVerified ? 'primary' : 'warning'}
                variant="outlined"
                label={profile.emailVerified ? 'Email verified' : 'Email not verified'}
              />
              <Chip size="small" variant="outlined" label={providerLabel(profile.provider)} />
              {profile.createdAt && <Chip size="small" variant="outlined" label={`Member since ${formatDate(profile.createdAt)}`} />}
            </Stack>
          </Box>
        </Stack>

        {notice && (
          <Alert severity={notice.severity} onClose={() => setNotice(null)}>
            {notice.text}
          </Alert>
        )}
        {account.isLoading && <CircularProgress size={22} />}

        <Section title="Account details">
          <EditableField
            label="Name"
            value={profile.name}
            disabledReason={!isPassword ? socialReason : !managed ? unmanagedReason : !profile.canChangeName ? socialReason : undefined}
            onSave={(name) => save({ name })}
          />
          <EditableField
            label="Email"
            value={profile.email}
            type="email"
            helper="We'll send a verification link to the new address."
            disabledReason={!isPassword ? socialReason : !managed ? unmanagedReason : !profile.canChangeEmail ? socialReason : undefined}
            onSave={(email) => save({ email })}
          />
          {!profile.emailVerified && managed && (
            <Row
              label="Email verification"
              value="Not verified yet"
              action={
                <Button variant="outlined" size="small" onClick={() => verify.mutate()} disabled={verify.isPending}>
                  Resend verification email
                </Button>
              }
            />
          )}
        </Section>

        <Section title="Security">
          <Row
            label="Password"
            value={isPassword ? '••••••••••' : `Managed by ${providerName}`}
            action={
              isPassword ? (
                <Button variant="outlined" size="small" onClick={() => passwordReset.mutate()} disabled={passwordReset.isPending}>
                  {passwordReset.isPending ? 'Sending…' : 'Change password'}
                </Button>
              ) : undefined
            }
          >
            {isPassword && (
              <Typography variant="caption" color="text.secondary">
                We'll email you a secure link to set a new password.
              </Typography>
            )}
          </Row>
          {profile.lastLogin && <Row label="Last sign-in" value={formatDate(profile.lastLogin)} />}
          <Row
            label="Session"
            value="Signed in on this device"
            action={
              <Button
                variant="outlined"
                size="small"
                startIcon={<Logout />}
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Log out
              </Button>
            }
          />
        </Section>

        <Section title="Preferences" description="Saved in this browser.">
          <Row
            label="Theme"
            action={
              <SegmentedToggle
                aria-label="Theme"
                value={mode}
                onChange={(m) => m !== mode && toggleColorMode()}
                options={[
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ]}
              />
            }
          />
          <Row
            label="Value calculator"
            value={
              calcPrefs.bankroll || calcPrefs.kelly
                ? `${calcPrefs.bankroll ? `Bankroll $${calcPrefs.bankroll}` : 'No bankroll'} · ${calcPrefs.kelly === '0.5' ? '½' : '¼'} Kelly`
                : 'Defaults (no bankroll, ¼ Kelly)'
            }
            action={
              <Button
                size="small"
                disabled={!calcPrefs.bankroll && !calcPrefs.kelly}
                onClick={() => {
                  try {
                    window.localStorage.removeItem(BANKROLL_KEY);
                    window.localStorage.removeItem(KELLY_KEY);
                  } catch {
                    /* storage unavailable */
                  }
                  setCalcPrefs({ bankroll: null, kelly: null });
                }}
              >
                Reset
              </Button>
            }
          />
        </Section>

        <Section title="Privacy and data">
          <Row
            label="Policies"
            value={
              <>
                <Link component={RouterLink} to="/privacy" underline="hover">
                  Privacy Policy
                </Link>{' '}
                ·{' '}
                <Link component={RouterLink} to="/terms" underline="hover">
                  Terms of Service
                </Link>
              </>
            }
          />
          <Row
            label="Your data"
            value={`For a copy of your information, email ${LEGAL_CONTACT_EMAIL}.`}
          />
        </Section>

        <Section title="Delete account" danger description="Permanently deletes your account and sign-in. This can't be undone.">
          <Row
            label="Delete your account"
            value={managed ? undefined : `Email ${LEGAL_CONTACT_EMAIL} from ${profile.email} and we'll delete it for you.`}
            action={
              managed ? (
                <Button variant="outlined" color="error" size="small" onClick={() => setDeleteOpen(true)}>
                  Delete account
                </Button>
              ) : undefined
            }
          />
        </Section>
      </Stack>

      <Dialog open={deleteOpen} onClose={() => !remove.isPending && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete your account?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This permanently deletes the account for <strong>{profile.email}</strong>. You'll be signed out, and you can't
            undo it. Type <strong>DELETE</strong> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            placeholder="DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteText !== 'DELETE' || remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Deleting…' : 'Delete account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
